import { BaseAction, CallResult, DataAction, MessageAction } from 'src/CustomTypes'
import { ProfileObject } from 'src/dataSources/geo/CrossSection'
import { CURRENT_PROFILE_VIEW, SET_COORDINATES, SET_MAX_DEPTH, SET_MAX_BOREHOLE_DISTANCE, FETCHING_PROFILE_STARTED, FETCHING_PROFILE_SUCCESS, FETCHING_PROFILE_FAILURE, CLEAR_PROFILE_STATE, SET_CROSSHAIR_DISTANCE, DISABLE_SETTING, ENABLE_SETTING } from '../actions/ProfileActions'

export enum ProfileView {
  Empty,
  Info
}

export enum ProfileSetting {
  SHOW_BOREHOLE_LABEL = 'showBoreholeLabel',
  SHOW_GEOLOGY_LABEL = 'showGeologyLabel',
  SHOW_FILTER_LEVEL = 'showFilterLevel',
  SHOW_WATER_LEVEL_SYMBOL = 'showWaterLevelSymbol',
  SHOW_WATER_LEVEL = 'showWaterLevel',
  SHOW_GEO_LOGO = 'showGeoLogo',
  SHOW_PROFILE_BUFFER = 'showProfileBuffer'
}

export interface ProfileState {
  fetchProfileStatus: CallResult
  fetchProfileError: string
  selectedProfile?: ProfileObject

  currentView: ProfileView
  coordinates: ol.Coordinate[]
  maxBoreholeDistance: number
  maxDepth: number
  crosshairDistance?: number
  enabledSettings: ProfileSetting[]
}

export const profileStateInitial: ProfileState = {
  fetchProfileStatus: CallResult.Initial,
  fetchProfileError: '',
  selectedProfile: undefined,

  currentView: ProfileView.Empty,
  coordinates: [],
  maxBoreholeDistance: 0,
  maxDepth: -40,
  crosshairDistance: undefined,
  enabledSettings: [
    ProfileSetting.SHOW_BOREHOLE_LABEL,
    ProfileSetting.SHOW_FILTER_LEVEL,
    ProfileSetting.SHOW_GEOLOGY_LABEL,
    ProfileSetting.SHOW_GEO_LOGO,
    ProfileSetting.SHOW_PROFILE_BUFFER,
    ProfileSetting.SHOW_WATER_LEVEL,
    ProfileSetting.SHOW_WATER_LEVEL_SYMBOL
  ]
}

export function profileReducer (
  state: ProfileState = profileStateInitial, // eslint-disable-line @typescript-eslint/default-param-last
  action: BaseAction
): ProfileState {
  switch (action.type) {
    case CLEAR_PROFILE_STATE:
      return {
        ...state,
        currentView: ProfileView.Empty,
        coordinates: [],
        selectedProfile: undefined
      }
    case CURRENT_PROFILE_VIEW: {
      const a = action as DataAction<ProfileView>
      return {
        ...state,
        currentView: a.data
      }
    }
    case DISABLE_SETTING: {
      const a = action as DataAction<ProfileSetting>
      return {
        ...state,
        enabledSettings: state.enabledSettings.filter(
          (setting) => setting !== a.data
        )
      }
    }
    case ENABLE_SETTING: {
      const a = action as DataAction<ProfileSetting>
      let enabledSettings = [...state.enabledSettings]
      const settingAlreadyEnabled = state.enabledSettings.some(
        (setting) => setting === a.data
      )
      if (!settingAlreadyEnabled) {
        enabledSettings = [...state.enabledSettings, a.data]
      }
      return {
        ...state,
        enabledSettings: enabledSettings
      }
    }
    case FETCHING_PROFILE_STARTED: {
      return {
        ...state,
        fetchProfileStatus: CallResult.Running
      }
    }
    case FETCHING_PROFILE_FAILURE: {
      const a = action as MessageAction
      return {
        ...state,
        fetchProfileStatus: CallResult.Failed,
        fetchProfileError: a.message
      }
    }
    case FETCHING_PROFILE_SUCCESS: {
      const a = action as DataAction<ProfileObject | undefined>
      return {
        ...state,
        fetchProfileStatus: CallResult.Succeeded,
        selectedProfile: a.data !== undefined ? a.data : undefined
      }
    }
    case SET_COORDINATES: {
      const a = action as DataAction<ol.Coordinate[]>
      return {
        ...state,
        coordinates: a.data
      }
    }
    case SET_MAX_BOREHOLE_DISTANCE: {
      const a = action as DataAction<number>
      return {
        ...state,
        maxBoreholeDistance: a.data
      }
    }
    case SET_MAX_DEPTH: {
      const a = action as DataAction<number>
      return {
        ...state,
        maxDepth: a.data
      }
    }
    case SET_CROSSHAIR_DISTANCE: {
      const a = action as DataAction<number>
      return {
        ...state,
        crosshairDistance: a.data
      }
    }
    default:
      return state
  }
}
